import React, { useState } from "react";
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Spinner,
    Badge,
} from "reactstrap";

/**
 * CompanyDeleteModal – Firma silme onayı
 *
 * Props
 * ─────────────────────────────────────────────────────────
 * @param {boolean}  isOpen      – Modal açık mı?
 * @param {Function} toggle      – Modal'ı kapat/aç
 * @param {Object}   company     – Silinecek firma bilgileri
 * @param {Function} onConfirm   – Silme onaylandığında çalışır
 */
export const CompanyDeleteModal = ({ isOpen, toggle, company, onConfirm }) => {
    const [deleting, setDeleting] = useState(false);

    if (!company) return null;

    const handleConfirm = async () => {
        if (!onConfirm || !company.id) return;

        setDeleting(true);
        try {
            // onConfirm fonksiyonunun bir Promise döndürdüğünü varsayıyoruz.
            await onConfirm(company.id);
        } catch (error) {
            console.error("Firma silinirken hata oluştu:", error);
            // Burada kullanıcıya bir hata mesajı da gösterilebilir.
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} centered>
            <ModalHeader toggle={toggle} className="border-bottom">
                <div className="d-flex align-items-center gap-2 text-danger">
                    <i className="bx bx-trash fs-4"></i>
                    Firma Silme Onayı
                </div>
            </ModalHeader>

            <ModalBody className="py-4">
                <div className="text-center mb-4">
                    <div className="mb-3">
                        <i className="bx bx-building-house display-4 text-muted"></i>
                    </div>
                    <p className="text-muted mb-2">Aşağıdaki firmayı kalıcı olarak silmek istediğinizden emin misiniz?</p>
                </div>

                {/* Firma Bilgileri Özeti */}
                <div className="border rounded p-3 bg-light">
                    <div className="row">
                        <div className="col-12 mb-2">
                            <div className="d-flex justify-content-between align-items-start">
                                <div>
                                    <h6 className="mb-1 text-dark">{company.companyName}</h6>
                                    <div className="text-muted small">
                                        Vergi No: <strong>{company.invoiceTaxNumber || "Belirtilmemiş"}</strong>
                                    </div>
                                </div>
                                <div className="text-end">
                                    {/* `company.enabled` gibi bir alanınız varsa kullanabilirsiniz */}
                                    <Badge color={company.active ? "success" : "danger"}>
                                        {company.active ? "Aktif" : "Pasif"}
                                    </Badge>
                                </div>
                            </div>
                        </div>

                        {/* İletişim Bilgileri (opsiyonel) */}
                        {(company.contactEmail || company.phoneNumber) && (
                            <div className="col-12 mt-2 pt-2 border-top">
                                <div className="text-muted small">
                                    {company.email && (
                                        <div>
                                            <i className="bx bx-envelope me-1"></i>
                                            {company.contactEmail}
                                        </div>
                                    )}
                                    {company.phoneNumber && (
                                        <div className="mt-1">
                                            <i className="bx bx-phone me-1"></i>
                                            {company.phoneNumber}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Bağlı Cari/Bayi Hesapları (opsiyonel) */}
                        {company.accounts && company.accounts.length > 0 && (
                            <div className="col-12 mt-2 pt-2 border-top">
                                <div className="text-muted small mb-1">Bağlı Cari/Bayi Hesapları:</div>
                                <div className="d-flex flex-wrap gap-1">
                                    {company.accounts.slice(0, 3).map((account, index) => (
                                        <Badge
                                            key={account.id || index}
                                            color="outline-secondary"
                                            className="small"
                                        >
                                            {account.accountName}
                                        </Badge>
                                    ))}
                                    {company.accounts.length > 3 && (
                                        <Badge color="outline-secondary" className="small">
                                            +{company.accounts.length - 3} diğer
                                        </Badge>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </ModalBody>

            <ModalFooter className="border-top">
                <div className="d-flex justify-content-end gap-2 w-100">
                    <Button
                        color="secondary"
                        onClick={toggle}
                        disabled={deleting}
                        className="rounded-pill px-4"
                    >
                        İptal
                    </Button>
                    <Button
                        color="danger"
                        onClick={handleConfirm}
                        disabled={deleting}
                        className="rounded-pill px-4 d-flex align-items-center gap-2"
                        style={{minWidth: "110px"}}
                    >
                        {deleting ? (
                            <>
                                <Spinner size="sm" />
                                Siliniyor...
                            </>
                        ) : (
                            <>
                                <i className="bx bx-trash"></i>
                                Evet, Sil
                            </>
                        )}
                    </Button>
                </div>
            </ModalFooter>
        </Modal>
    );
};